import { DateTime } from "luxon";
import { useMemo } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useGlobalManualLocationOrder } from "../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import {
	ALLOW_AVAILABILITY_FALLBACK,
	byDimensionsOf,
	type LadderFilter,
	resolveLocationCensus,
	scoreAdjustedRequiredForLocation,
} from "../../../utils/censusResolution";
import { demoRequiredForDay } from "../../../utils/demoRequiredStaff";
import { countsPunchesOnPastDays } from "../../../utils/demoStaffingRules";
import { type DemoRequiredInput, shiftDaysOnDate } from "../../../utils/hospitalDemoScore";
import {
	type DayItem,
	type DepartmentLocation,
	type NurseFullRole,
	type NurseInShift,
	type NurseRole,
	type StatsMode,
	type TimeOffRequest,
	type WeekData,
	WeeklyGroupingType,
} from "../../../utils/models";
import { isPastDayWithPunches } from "../../../utils/pastDayStats";
import { filterNursesForCoverage, INCLUDE_TRAINEES_IN_COVERAGE } from "../../../utils/statsCalculations";

/**
 * What the census resolver needs for one day: the dimension map and the department total.
 *
 * `demoRequired` is the demo-only adjustment (patient score or node split), so this grid matches
 * Shift Insights and the other views. `null` for non-demo departments.
 */
export type DayCensusInput = { byDimensions: Record<string, number>; totalCensus: number; demoRequired: DemoRequiredInput };

const EMPTY_DAY_CENSUS: DayCensusInput = { byDimensions: {}, totalCensus: 0, demoRequired: null };

/**
 * Bridge the weekly view's optional role/job filter onto the resolver's required, total one. An
 * absent filter means "the location total across every dimension", which used to be expressed by
 * simply omitting the argument.
 */
const toLadderFilter = (filter?: { roles?: NurseRole[]; jobId?: string }): LadderFilter => {
	if (filter?.jobId !== undefined) return { kind: "job", jobId: filter.jobId };
	if (filter?.roles !== undefined) return { kind: "roles", roles: filter.roles };
	return { kind: "all" };
};

import { compareValues } from "../../../utils/multiColumnSorting";
import { mergeIntoCanonicalGroups } from "../../../utils/timeGrouping";
import { getCensus, getShortRole, inverseRolesMap, rolesMap } from "../../../utils/utilFunctions";
import { useGetDepartmentTimeOffRequests } from "../../RequestTimeOff/timeOffRequestsHooks";
import { matchesContractSections } from "../../team/config";
import { createPostedRequestMatcher, type PostedMatch, type TryMatchFn } from "./usePostedRequestMatcher";

const WEEKDAY_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

// Stable reference for the default-empty sections filter so React memoization
// upstream isn't invalidated by a new Set being created on every render.
const EMPTY_SECTIONS: ReadonlySet<string> = new Set();

function getDayName(date: import("luxon").DateTime): string {
	return WEEKDAY_NAMES[date.weekdayISO];
}

// ─── Time formatting helpers ─────────────────────────────────────────────────

function formatMin(minutes: number): string {
	const h = Math.floor(minutes / 60) % 24;
	const m = minutes % 60;
	const period = h >= 12 ? "p" : "a";
	const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
	return m === 0 ? `${displayHour}${period}` : `${displayHour}:${m.toString().padStart(2, "0")}${period}`;
}

function formatMinutesToLabel(startMin: number, endMin: number): string {
	return `${formatMin(startMin)}-${formatMin(endMin)}`;
}

function parseHHMMToMinutes(hhMM: string): number {
	const [h, m] = hhMM.split(":").map(Number);
	return h * 60 + (m || 0);
}

/**
 * When a posted shift's hours differ from the section's hours, returns a label like "6a-6p"
 * (shown next to "Posted"). Mirrors the rule used for missing-slot labels so the visual
 * treatment is consistent.
 */
function postedLabelRelativeToGroup(match: PostedMatch | null, group: { startMin: number; endMin: number }): string | null {
	if (!match) return null;
	if (match.postedStartMin === group.startMin && match.postedEndMin === group.endMin) return null;
	return formatMinutesToLabel(match.postedStartMin, match.postedEndMin);
}

// ─── Overlap utilities ───────────────────────────────────────────────────────

/**
 * Computes overlap in minutes between two time intervals, correctly handling
 * overnight intervals (where end < start in clock time).
 * Both intervals are split into at most two linear segments [0-1440] before
 * comparing, so all wrap-around cases are handled correctly.
 */
function computeOverlapMinutes(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
	const aSegs: [number, number][] =
		aEnd > aStart
			? [[aStart, aEnd]]
			: [
					[aStart, 1440],
					[0, aEnd],
				];
	const bSegs: [number, number][] =
		bEnd > bStart
			? [[bStart, bEnd]]
			: [
					[bStart, 1440],
					[0, bEnd],
				];
	let total = 0;
	for (const [as, ae] of aSegs) {
		for (const [bs, be] of bSegs) {
			total += Math.max(0, Math.min(ae, be) - Math.max(as, bs));
		}
	}
	return total;
}

function durationMinutes(startMin: number, endMin: number): number {
	return endMin > startMin ? endMin - startMin : endMin + 1440 - startMin;
}

/** Returns true if [shiftStart, shiftEnd] is fully contained within [groupStart, groupEnd]. */
function isFullyContained(shiftStart: number, shiftEnd: number, groupStart: number, groupEnd: number): boolean {
	return computeOverlapMinutes(shiftStart, shiftEnd, groupStart, groupEnd) >= durationMinutes(shiftStart, shiftEnd);
}

// ─── Time group derivation ───────────────────────────────────────────────────

type TimeGroupConfig = { startMin: number; endMin: number; label: string };

/**
 * Derives canonical time groups from the actual scheduled shifts for one or more roles.
 * Groups are ordered by popularity (most common times first).
 */
function computeTimeGroups(weekViewData: DayItem[], roles: NurseFullRole[], filterByJobId?: string, disableTimeGroupsOverlaps?: boolean): TimeGroupConfig[] {
	const rolesSet = new Set(roles);
	const freq = new Map<string, { startMin: number; endMin: number; count: number }>();

	for (const { shifts } of weekViewData) {
		for (const s of shifts) {
			if (filterByJobId ? !isShiftApplicableToJob(s, filterByJobId) : !rolesSet.has(s.role)) continue;
			const startMin = s.startTime.hour * 60 + s.startTime.minute;
			const endMin = s.endTime.hour * 60 + s.endTime.minute;
			const key = `${startMin}-${endMin}`;
			const entry = freq.get(key);
			if (entry) {
				entry.count++;
			} else {
				freq.set(key, { startMin, endMin, count: 1 });
			}
		}
	}

	// Sort most-popular first; most popular times become the canonical group anchors
	const sorted = [...freq.values()].sort((a, b) => b.count - a.count);
	const windows = sorted.map(({ startMin, endMin }) => ({ startMin, endMin, label: formatMinutesToLabel(startMin, endMin) }));
	return mergeIntoCanonicalGroups(windows, disableTimeGroupsOverlaps ? { maxOverlapMinutes: 60 } : undefined);
}

/**
 * Builds additional time groups from location config times for configs that
 * couldn't be matched to any shift-derived group. Uses the same merge logic.
 */
function buildConfigDerivedGroups(unboundConfigs: DepartmentLocation[]): TimeGroupConfig[] {
	const windows = unboundConfigs.map((cfg) => {
		const startMin = parseHHMMToMinutes(cfg.shift_start);
		const endMin = parseHHMMToMinutes(cfg.shift_end);
		return { startMin, endMin, label: formatMinutesToLabel(startMin, endMin) };
	});
	return mergeIntoCanonicalGroups(windows);
}

// ─── Shift → group assignment ────────────────────────────────────────────────

/**
 * Finds the best matching group for a time segment using priority order:
 *   1. Same start time
 *   2. Same end time
 *   3. Most overlap minutes
 *   @param groups - candidate groups to match against
 *   @param usedIndices - groups that have already been assigned in the current iteration and should be skipped
 *   @param segStart - the time segment to find a group for
 *   @param segEnd - the time segment to find a group for
 * Returns -1 if no group has any overlap with the segment.
 */
function findBestGroupForSegment(segStart: number, segEnd: number, groups: TimeGroupConfig[], usedIndices: Set<number>): number {
	// Priority 1: same start time
	for (let i = 0; i < groups.length; i++) {
		if (usedIndices.has(i)) continue;
		if (computeOverlapMinutes(segStart, segEnd, groups[i].startMin, groups[i].endMin) === 0) continue;
		if (groups[i].startMin === segStart) return i;
	}
	// Priority 2: same end time
	for (let i = 0; i < groups.length; i++) {
		if (usedIndices.has(i)) continue;
		if (computeOverlapMinutes(segStart, segEnd, groups[i].startMin, groups[i].endMin) === 0) continue;
		if (groups[i].endMin === segEnd) return i;
	}
	// Priority 3: most overlap minutes
	let bestIdx = -1;
	let bestOverlap = 0;
	for (let i = 0; i < groups.length; i++) {
		if (usedIndices.has(i)) continue;
		const overlap = computeOverlapMinutes(segStart, segEnd, groups[i].startMin, groups[i].endMin);
		if (overlap > bestOverlap) {
			bestOverlap = overlap;
			bestIdx = i;
		}
	}
	return bestIdx;
}

/** The smallest group that fully contains [start, end], or -1 when none does. */
function findSmallestContainingGroup(start: number, end: number, groups: TimeGroupConfig[]): number {
	let containingIdx = -1;
	let bestDuration = Infinity;
	for (let i = 0; i < groups.length; i++) {
		if (isFullyContained(start, end, groups[i].startMin, groups[i].endMin)) {
			const gDur = durationMinutes(groups[i].startMin, groups[i].endMin);
			if (gDur < bestDuration) {
				bestDuration = gDur;
				containingIdx = i;
			}
		}
	}
	return containingIdx;
}

function pushWeightedShift(locMap: Map<string, WeightedShift[]>, locationName: string, weighted: WeightedShift): void {
	const existing = locMap.get(locationName);
	if (existing) existing.push(weighted);
	else locMap.set(locationName, [weighted]);
}

/**
 * For a single day, assigns each matching role shift to the groups it belongs to.
 *
 * Assignment rules:
 *   - If a group fully contains the shift → assign to that group only (weight 1.0).
 *   - Otherwise → best-match-first with pending-segments queue:
 *       Find the best group for the current segment (same start → same end → most overlap),
 *       assign with weight = overlap(originalShift, group) / groupDuration,
 *       then push both the left and right uncovered portions back as pending segments.
 *
 * The caller (buildRoleSection) guarantees via a pre-scan that every shift has at
 * least one overlapping group, so bestIdx will not be -1 on the first iteration.
 * Returns: groupIdx → Map<locationName, WeightedShift[]>
 */
function buildShiftsByGroupForDay(
	dayShifts: NurseInShift[],
	roles: NurseFullRole[],
	groups: TimeGroupConfig[],
	filterByJobId?: string,
): Map<string, WeightedShift[]>[] {
	const rolesSet = new Set(roles);
	const groupShifts: Map<string, WeightedShift[]>[] = groups.map(() => new Map());

	for (const shift of dayShifts) {
		if (filterByJobId ? !isShiftApplicableToJob(shift, filterByJobId) : !rolesSet.has(shift.role)) continue;

		const shiftStart = shift.startTime.hour * 60 + shift.startTime.minute;
		const shiftEnd = shift.endTime.hour * 60 + shift.endTime.minute;
		const locationName = shift.location?.trim() ?? "";

		// Case 1: find smallest fully-containing group → weight 1.0
		const containingIdx = findSmallestContainingGroup(shiftStart, shiftEnd, groups);
		if (containingIdx >= 0) {
			pushWeightedShift(groupShifts[containingIdx], locationName, { shift, weight: 1.0, isActual: false });
			continue;
		}

		// Case 2: best-match-first with pending-segments queue.
		// We track every uncovered portion of the shift as a [start, end] tuple.
		// After each group assignment, both the left and right leftovers are pushed
		// back so neither side is silently dropped when the best group is in the middle.
		const usedIndices = new Set<number>();
		const pendingSegments: [number, number][] = [[shiftStart, shiftEnd]];

		// Cap total iterations at groups.length: each iteration consumes one group
		// via usedIndices, so the loop is guaranteed to terminate.
		while (pendingSegments.length > 0) {
			const seg = pendingSegments.shift();
			if (!seg) break;
			const [segStart, segEnd] = seg;

			const bestIdx = findBestGroupForSegment(segStart, segEnd, groups, usedIndices);

			if (bestIdx === -1) {
				// No group overlaps this segment. Acceptable for sub-segments (iter > 0)
				// because the shift is already visible in at least one group.
				continue;
			}

			usedIndices.add(bestIdx);
			const g = groups[bestIdx];

			// Weight = overlap of the TRIMMED segment with this group / group duration.
			// Using the segment (not the original shift) avoids double-counting time
			// already covered by a previously assigned group when groups overlap.
			const overlap = computeOverlapMinutes(segStart, segEnd, g.startMin, g.endMin);
			const weight = Math.min(1, Math.floor((overlap / durationMinutes(g.startMin, g.endMin)) * 10) / 10);

			pushWeightedShift(groupShifts[bestIdx], locationName, { shift, weight, isActual: false });

			// Push left leftover: portion of this segment before the group starts
			if (segStart < g.startMin) pendingSegments.push([segStart, g.startMin]);
			// Push right leftover: portion of this segment after the group ends
			if (segEnd > g.endMin) pendingSegments.push([g.endMin, segEnd]);
		}
	}

	return groupShifts;
}

// ─── Punched shifts (past days) ──────────────────────────────────────────────

/** A punched shift and the shift type (Day / Night) the backend filed it under. */
export type PunchedShift = { shift: NurseInShift; shiftType: string };

/**
 * Per day (same order as `weekViewData`): the punched shifts to show instead of the schedule, or
 * `null` to show the schedule. `noGroup` collects the punched shifts that fit no group.
 */
export type PunchedShiftsByDay = { byDay: readonly (readonly PunchedShift[] | null)[]; noGroup: Set<PunchedShift> };

/**
 * Every punched shift on `date`, with its shift type, found by date across the loaded weeks.
 * Nothing is filtered here: like scheduled shifts, every punch is shown (a missing punch-out is a
 * zero-length row, e.g. a Night nurse still on the clock) and `weightedActual` decides what counts.
 */
export function punchedShiftsOnDate(weeks: readonly WeekData[], date: DateTime): PunchedShift[] {
	return shiftDaysOnDate(weeks, date).flatMap(({ shiftType, day }) =>
		// No `nursesActual` means nobody punched on that shift.
		(day.nursesActual ?? []).map((shift) => ({ shift, shiftType })),
	);
}

const MINUTES_PER_DAY = 24 * 60;

/** Minutes between two times of day, the short way round midnight. */
const minutesApart = (a: number, b: number): number => {
	const gap = Math.abs(a - b) % MINUTES_PER_DAY;
	return Math.min(gap, MINUTES_PER_DAY - gap);
};

/** Minutes two real time ranges share. */
const sharedMinutes = (aStart: DateTime, aEnd: DateTime, bStart: DateTime, bEnd: DateTime): number =>
	Math.max(0, Math.min(aEnd.toMillis(), bEnd.toMillis()) - Math.max(aStart.toMillis(), bStart.toMillis())) / 60_000;

/**
 * The configured window of the row a punched shift belongs to: same location and job, and the row
 * whose window on `day` holds most of the punch (ties, e.g. a zero-length punch: the start nearest
 * the clock-in). Real dates, not clock times, so a punch that runs past midnight or 24 hours still
 * counts where it actually was. Not the Day/Night the punch was filed under: that comes from the
 * clock-in time alone, so a Day nurse clocking in at 05:46 is filed as Night. The row's window is
 * used, not the punch times, so a long punch (e.g. 05:45–18:14) still lands in one group.
 *
 * DEFAULT: when no row matches the location and job, all rows are candidates, so the punched nurse
 * is still shown in a shift.
 */
function punchedShiftWindow({ shift }: PunchedShift, locations: readonly DepartmentLocation[], day: DateTime): { startMin: number; endMin: number } | null {
	const clockIn = shift.startTime.hour * 60 + shift.startTime.minute;
	const dayStart = day.startOf("day");
	const sameRow = locations.filter((loc) => loc.location.trim() === shift.location?.trim() && loc.jobId === shift.jobId);
	const candidates = (sameRow.length > 0 ? sameRow : locations).map((loc) => {
		const startMin = parseHHMMToMinutes(loc.shift_start);
		const endMin = parseHHMMToMinutes(loc.shift_end);
		const rowStart = dayStart.plus({ minutes: startMin });
		const rowEnd = dayStart.plus({ minutes: endMin > startMin ? endMin : endMin + MINUTES_PER_DAY });
		return { startMin, endMin, overlap: sharedMinutes(shift.startTime, shift.endTime, rowStart, rowEnd), distance: minutesApart(startMin, clockIn) };
	});
	const best = candidates.reduce<(typeof candidates)[number] | undefined>(
		(current, candidate) =>
			current === undefined || candidate.overlap > current.overlap || (candidate.overlap === current.overlap && candidate.distance < current.distance)
				? candidate
				: current,
		undefined,
	);
	return best === undefined ? null : { startMin: best.startMin, endMin: best.endMin };
}

/** The smallest group containing the window, else the best-matching one (-1 when none overlaps). */
function findGroupForWindow(start: number, end: number, groups: TimeGroupConfig[]): number {
	const containingIdx = findSmallestContainingGroup(start, end, groups);
	return containingIdx >= 0 ? containingIdx : findBestGroupForSegment(start, end, groups, new Set());
}

/**
 * Like `buildShiftsByGroupForDay`, but for a past day's punched shifts: each one goes to the group of
 * its row's window at weight 1.0. The cell still shows the real punch times. A shift that fits no
 * group is skipped and added to `noGroup`.
 */
function buildPunchedShiftsByGroupForDay(
	day: DateTime,
	punched: readonly PunchedShift[],
	noGroup: Set<PunchedShift>,
	roles: NurseFullRole[],
	groups: TimeGroupConfig[],
	locations: readonly DepartmentLocation[],
	filterByJobId?: string,
): Map<string, WeightedShift[]>[] {
	const rolesSet = new Set(roles);
	const groupShifts: Map<string, WeightedShift[]>[] = groups.map(() => new Map());
	for (const entry of punched) {
		const { shift } = entry;
		if (filterByJobId ? !isShiftApplicableToJob(shift, filterByJobId) : !rolesSet.has(shift.role)) continue;
		const window = punchedShiftWindow(entry, locations, day);
		const groupIdx = window === null ? -1 : findGroupForWindow(window.startMin, window.endMin, groups);
		if (groupIdx === -1) {
			noGroup.add(entry);
			continue;
		}
		pushWeightedShift(groupShifts[groupIdx], shift.location?.trim() ?? "", { shift, weight: 1.0, isActual: true });
	}
	return groupShifts;
}

/** Each day's shifts by group: punched shifts on past days that have them, scheduled otherwise. */
function buildShiftsByGroupForWeek(
	weekViewData: DayItem[],
	punchedShiftsByDay: PunchedShiftsByDay | null,
	roles: NurseFullRole[],
	groups: TimeGroupConfig[],
	locations: readonly DepartmentLocation[],
	filterByJobId?: string,
): Map<string, WeightedShift[]>[][] {
	return weekViewData.map((dayItem, dayIdx) => {
		const punched = punchedShiftsByDay?.byDay[dayIdx];
		return punchedShiftsByDay && punched
			? buildPunchedShiftsByGroupForDay(dayItem.date, punched, punchedShiftsByDay.noGroup, roles, groups, locations, filterByJobId)
			: buildShiftsByGroupForDay(dayItem.shifts, roles, groups, filterByJobId);
	});
}

// ─── Types ───────────────────────────────────────────────────────────────────

/** A demo setup problem found while building the week, for the component to report. */
export type WeeklyGapDataProblem = { event: string; properties: Record<string, string | number> };

/** `isActual` marks a punched shift from a past day. */
export type WeightedShift = { shift: NurseInShift; weight: number; isActual: boolean };

export type NurseSlot = {
	name: string;
	/** True when the nurse's actual shift hours differ from the group's representative times */
	showHours: boolean;
	hoursLabel: string;
	nurseInShift: NurseInShift;
	/** Fractional staffing contribution: 1.0 for exact/contained shifts, <1.0 for partial overlap */
	weight: number;
	/** Time off request that overlaps this cell's date, if any */
	timeOffRequest?: TimeOffRequest;
	/** True for a punched (actual) shift on a past day; its menu then acts on the actual row. */
	isActual: boolean;
};

export type RoleCount = {
	role: string;
	actual: number;
	required: number;
};

export type MissingSlotData = {
	timeLabel: string | null;
	postedRequestId: string | null;
	/** When the posted shift's hours differ from the section's hours, the posted hours label (e.g. "6a-6p"). */
	postedTimeLabel: string | null;
	/** Slot-specific start (config's hours when known; falls back to section start). Used to pre-fill the post-shift dialog on click. */
	startMin: number;
	/** Slot-specific end (config's hours when known; falls back to section end). */
	endMin: number;
};

export type LocationDayData = {
	required: number;
	isBlocked: boolean;
	nurses: NurseSlot[];
	/** One entry per missing slot */
	missingSlotLabels: MissingSlotData[];
	/** Present when the location has multi-role configs — per-role actual/required breakdown */
	roleCounts?: RoleCount[];
};

export type ComputedLocation = {
	locationName: string;
	locationShortName: string;
	/** All DepartmentLocation configs for this location name under this time group */
	configs: DepartmentLocation[];
	/** One entry per weekViewData day (same order) */
	dayData: LocationDayData[];
	/** max(maxRequired, maxActualNurses) across non-blocked days */
	numSlots: number;
};

export type TimeTitle = {
	label: string;
	startMin: number;
	endMin: number;
	locations: ComputedLocation[];
	/** Aggregate actual/required per day */
	dayTotals: { actual: number; required: number }[];
};

export type RoleSection = {
	/** NurseRole short string, role group name, or job display name */
	shortRole: string;
	timeTitles: TimeTitle[];
	/** True when this section groups by job (vs role/role group) */
	isJobSection?: boolean;
	/** The section's job; set only on job sections so a Missing click can prefill that job when posting */
	jobId?: string;
};

// ─── Coverage-eligibility helper ──────────────────────────────────────────────

/**
 * Whether a shift counts toward filling a required slot.
 *
 * Delegates to the app-wide `filterNursesForCoverage` rather than re-stating the rule. This file
 * used to carry its own `isTrainingOrOrientation` copy that checked training status but not
 * `isPendingToDelete`, so a shift already queued for removal still covered a slot here while the
 * gap finder and the staff table disagreed. One predicate, so the views cannot drift apart again.
 */
const countsAsCoverage = (shift: NurseInShift): boolean => filterNursesForCoverage(shift, INCLUDE_TRAINEES_IN_COVERAGE);

// ─── Weighted-actual helper ───────────────────────────────────────────────────

/** Sums fractional shift weights rather than counting cards, preventing false overstaffing.
 *  Excludes trainees and pending-delete shifts, which don't fill a staffing gap. */
export function weightedActual(slots: NurseSlot[]): number {
	return slots.reduce((sum, n) => sum + (countsAsCoverage(n.nurseInShift) ? n.weight : 0), 0);
}

// ─── Multi-role required helpers ─────────────────────────────────────────────

function getShortRoleName(fullRole: NurseFullRole): string {
	return getShortRole(fullRole);
}

/**
 * Determines the most frequent role for a location across the week.
 * Returns null if there are fewer than 2 distinct roles with configs.
 */
function getMostFrequentRole(weekNurses: NurseInShift[][], configRoles: NurseFullRole[]): NurseFullRole | null {
	if (configRoles.length < 2) return null;

	const roleCounts = new Map<NurseFullRole, number>();
	for (const role of configRoles) {
		roleCounts.set(role, 0);
	}
	for (const dayNurses of weekNurses) {
		for (const n of dayNurses) {
			if (roleCounts.has(n.role)) {
				roleCounts.set(n.role, (roleCounts.get(n.role) ?? 0) + 1);
			}
		}
	}

	let maxRole: NurseFullRole = configRoles[0];
	let maxCount = -1;
	for (const [role, count] of roleCounts) {
		if (count > maxCount) {
			maxCount = count;
			maxRole = role;
		}
	}
	return maxRole;
}

/**
 * Splits a multi-role config's single combined `totalRequired` into a per-role actual/required
 * breakdown for one day. The ladder gives ONE requirement for the role set (no per-role split), so
 * we distribute it across roles by actual staffing:
 *  - Non-frequent roles take `required = min(actual, remaining budget)` — meeting the total with a
 *    different role mix shows no false gap, but an overstaffed role is capped (surplus shown, not
 *    counted as required).
 *  - The frequent role absorbs whatever budget remains.
 * By construction the per-role requireds sum to `totalRequired` (never more), so `manipulatedRequired`
 * equals `totalRequired` — the group total and the per-role badges stay consistent and match the
 * daily view. Actual counts are weighted sums to reflect partial-overlap shifts.
 */
export function computeMultiRoleDay(
	nurses: NurseSlot[],
	totalRequired: number,
	configRoles: NurseFullRole[],
	frequentRole: NurseFullRole,
): { manipulatedRequired: number; roleCounts: RoleCount[] } {
	// Sum weighted actuals per role, excluding training/orientation nurses
	const actualByRole = new Map<NurseFullRole, number>();
	for (const role of configRoles) {
		actualByRole.set(role, 0);
	}
	for (const n of nurses) {
		if (countsAsCoverage(n.nurseInShift) && actualByRole.has(n.nurseInShift.role)) {
			actualByRole.set(n.nurseInShift.role, (actualByRole.get(n.nurseInShift.role) ?? 0) + n.weight);
		}
	}

	// Non-frequent roles: required = their actual, capped at the remaining budget so the total is
	// never exceeded (an overstaffed role shows a surplus instead of inflating the requirement).
	let remainingBudget = totalRequired;
	const roleCounts: RoleCount[] = [];

	for (const role of configRoles) {
		if (role === frequentRole) continue;
		const actual = actualByRole.get(role) ?? 0;
		const required = Math.min(actual, Math.max(0, remainingBudget));
		remainingBudget -= required;
		roleCounts.push({ role: getShortRoleName(role), actual, required });
	}

	// Frequent role: absorbs whatever budget remains.
	const frequentActual = actualByRole.get(frequentRole) ?? 0;
	const frequentRequired = Math.max(0, remainingBudget);
	roleCounts.push({ role: getShortRoleName(frequentRole), actual: frequentActual, required: frequentRequired });

	// Per-role requireds sum to totalRequired by construction — no inflation when overstaffed.
	const manipulatedRequired = roleCounts.reduce((sum, rc) => sum + rc.required, 0);

	return { manipulatedRequired, roleCounts };
}

// ─── Location computation ────────────────────────────────────────────────────

/**
 * Builds ComputedLocation entries for a group.
 * Location rows = union of (locations with shifts in this group) + (locations with configs in this group).
 * Shifts drive what is rendered; configs drive the required count only.
 */
function buildComputedLocationsForGroup(
	configsMap: Map<string, DepartmentLocation[]>,
	groupIdx: number,
	group: TimeGroupConfig,
	shiftsByDayAndGroup: Map<string, WeightedShift[]>[][],
	weekViewData: DayItem[],
	getNurseFormattedName: (id: number) => string,
	isGrouped: boolean,
	jobManagementEnabled: boolean,
	sectionRoles: NurseFullRole[],
	deptStatsMode: StatsMode,
	allRoleShiftsByDayAndGroup?: Map<string, WeightedShift[]>[][],
	dayCensusInputs?: DayCensusInput[],
	tryMatch?: TryMatchFn,
	customLocationOrder?: Map<string, number> | null,
	timeOffByNurse?: Map<number, TimeOffRequest[]>,
	requiredFilter?: { roles?: NurseRole[]; jobId?: string },
): ComputedLocation[] {
	const results: ComputedLocation[] = [];

	// Collect all location names: from shift buckets (across all days) + from configs
	const allLocationNames = new Set<string>(configsMap.keys());
	for (let dayIdx = 0; dayIdx < weekViewData.length; dayIdx++) {
		for (const locName of shiftsByDayAndGroup[dayIdx][groupIdx].keys()) {
			allLocationNames.add(locName);
		}
	}

	for (const locationName of allLocationNames) {
		const configs = configsMap.get(locationName) ?? [];
		const locationShortName = configs[0]?.location_short_name || locationName;

		// Determine if this location has multi-role configs
		const allConfigRoles = new Set<NurseFullRole>();
		for (const cfg of configs) {
			for (const r of cfg.roles) {
				if (r !== "all") allConfigRoles.add(r);
			}
		}
		const configRolesArr = [...allConfigRoles];
		const isMultiRole = configRolesArr.length > 1;

		// Collect section-specific weighted shifts per day (used for rendering)
		const weekWeightedShifts: WeightedShift[][] = weekViewData.map((_, dayIdx) => shiftsByDayAndGroup[dayIdx][groupIdx].get(locationName) ?? []);

		// For multi-role frequency: use all-role shifts if available, otherwise fall back to section shifts
		const weekWeightedAllRoles: WeightedShift[][] = allRoleShiftsByDayAndGroup
			? weekViewData.map((_, dayIdx) => allRoleShiftsByDayAndGroup[dayIdx][groupIdx]?.get(locationName) ?? [])
			: weekWeightedShifts;

		const frequentRole = isMultiRole
			? getMostFrequentRole(
					weekWeightedAllRoles.map((day) => day.map((ws) => ws.shift)),
					configRolesArr,
				)
			: null;

		const dayData: LocationDayData[] = weekViewData.map((_, dayIdx) => {
			const dayName = getDayName(weekViewData[dayIdx].date).toLowerCase();
			const shiftsHere = weekWeightedShifts[dayIdx];
			const activeConfigs = configs.filter((c) => c.days.includes(dayName));

			// Blocked only when no config is active AND no shifts exist on this day
			if (activeConfigs.length === 0 && shiftsHere.length === 0) {
				return { required: 0, isBlocked: true, nurses: [], missingSlotLabels: [] };
			}

			const dayCensus = dayCensusInputs?.[dayIdx] ?? EMPTY_DAY_CENSUS;
			const totalRequired = activeConfigs.reduce(
				(sum, cfg) =>
					sum +
					// Demo-adjusted per this config (its shift's score, or its node share). Demo only.
					scoreAdjustedRequiredForLocation(
						cfg,
						resolveLocationCensus(cfg, dayCensus.byDimensions, dayCensus.totalCensus, deptStatsMode, ALLOW_AVAILABILITY_FALLBACK),
						deptStatsMode,
						dayName,
						toLadderFilter(requiredFilter),
						dayCensus.demoRequired,
					),
				0,
			);

			const dayDate = weekViewData[dayIdx].date;
			const nurses: NurseSlot[] = shiftsHere.map(({ shift: s, weight, isActual }) => {
				const name = getNurseFormattedName(s.nurseId);
				const shiftStart = s.startTime.hour * 60 + s.startTime.minute;
				const shiftEnd = s.endTime.hour * 60 + s.endTime.minute;
				const showHours = shiftStart !== group.startMin || shiftEnd !== group.endMin;
				const hoursLabel = showHours ? formatMinutesToLabel(shiftStart, shiftEnd) : "";
				const timeOffRequest = timeOffByNurse?.get(s.nurseId)?.find((r) => dayDate >= r.startDate.startOf("day") && dayDate <= r.endDate.endOf("day"));
				return { name, showHours, hoursLabel, nurseInShift: s, weight, timeOffRequest, isActual };
			});

			// Handle multi-role required distribution
			if (isMultiRole && frequentRole) {
				if (isGrouped) {
					// Job Management: the ladder gives one combined requirement for the role set, and the
					// per-role split below is a fabricated estimate (distributes it by who happens to be
					// scheduled) that doesn't match the job-centric mental model this flag commits to
					// elsewhere (see applyJobManagementGrouping). Keep the single combined total instead of
					// attaching roleCounts, so LocationHeaderRow / RequiredBreakdownPopover fall back to
					// their existing single-badge rendering.
					if (jobManagementEnabled) {
						const required = totalRequired;
						// Match the old per-role path's actualByRole filtering (computeMultiRoleDay): only
						// nurses whose role is a declared member of this location's config count toward
						// actual staffing, so a stray shift from an out-of-config role doesn't reduce the
						// missing count here while it would've been ignored by the flag-off path.
						const configRoleNurses = nurses.filter((n) => configRolesArr.includes(n.nurseInShift.role));
						const missingCount = Math.max(0, required - weightedActual(configRoleNurses));
						const tryMatchResult =
							tryMatch?.(locationName, weekViewData[dayIdx].date, group.startMin, group.endMin, sectionRoles, missingCount, requiredFilter?.jobId ?? null) ??
							null;
						const postedTimeLabel = postedLabelRelativeToGroup(tryMatchResult, group);
						const missingSlotLabels: MissingSlotData[] = Array.from({ length: missingCount }, (_, index) => {
							const matched = tryMatchResult != null && index < tryMatchResult.providedAmountAfterMatching;
							return {
								timeLabel: null,
								postedRequestId: matched ? tryMatchResult.requestId : null,
								postedTimeLabel: matched ? postedTimeLabel : null,
								startMin: group.startMin,
								endMin: group.endMin,
							};
						});

						return { required, isBlocked: false, nurses, missingSlotLabels };
					}

					// Grouped mode: all roles' shifts are in this section, use nurses directly
					const { manipulatedRequired, roleCounts } = computeMultiRoleDay(nurses, totalRequired, configRolesArr, frequentRole);
					const required = manipulatedRequired;
					const frequentCount = roleCounts.find((rc) => rc.role === getShortRoleName(frequentRole));
					const frequentMissing = required ? (frequentCount ? Math.max(0, frequentCount.required - frequentCount.actual) : 0) : 0;
					const tryMatchResult =
						tryMatch?.(locationName, weekViewData[dayIdx].date, group.startMin, group.endMin, sectionRoles, frequentMissing, requiredFilter?.jobId ?? null) ??
						null;
					const postedTimeLabel = postedLabelRelativeToGroup(tryMatchResult, group);
					const missingSlotLabels: MissingSlotData[] = Array.from({ length: frequentMissing }, (_, index) => {
						const matched = tryMatchResult != null && index < tryMatchResult.providedAmountAfterMatching;
						return {
							timeLabel: null,
							postedRequestId: matched ? tryMatchResult.requestId : null,
							postedTimeLabel: matched ? postedTimeLabel : null,
							startMin: group.startMin,
							endMin: group.endMin,
						};
					});

					return { required: required, isBlocked: false, nurses, missingSlotLabels, roleCounts };
				}

				// Non-grouped mode: this section shows one role's shifts.
				// When the location has a ratio ladder, `totalRequired` is already filtered to this
				// section's role (its rungs), so it IS this role's requirement — no actual-based
				// redistribution needed. Without a ladder the fixed amount is shared across the
				// location's roles, so split it by actual staffing to estimate this role's share.
				const locationHasLadder = activeConfigs.some((cfg) => cfg.ratioLadder?.length);
				let myRequired: number;
				if (locationHasLadder) {
					myRequired = totalRequired;
				} else {
					const allRoleSlots: NurseSlot[] = weekWeightedAllRoles[dayIdx].map(({ shift: s, weight, isActual }) => ({
						name: "",
						showHours: false,
						hoursLabel: "",
						nurseInShift: s,
						weight,
						isActual,
					}));
					const { roleCounts } = computeMultiRoleDay(allRoleSlots, totalRequired, configRolesArr, frequentRole);
					// Find the role count for this section's role
					const sectionRole = sectionRoles[0];
					const sectionShortRole = getShortRoleName(sectionRole);
					myRequired = roleCounts.find((rc) => rc.role === sectionShortRole)?.required ?? 0;
				}

				// Missing slots for this section's role only
				const myMissing = Math.max(0, myRequired - weightedActual(nurses));
				const tryMatchResult =
					tryMatch?.(locationName, weekViewData[dayIdx].date, group.startMin, group.endMin, sectionRoles, myMissing, requiredFilter?.jobId ?? null) ?? null;
				const postedTimeLabel = postedLabelRelativeToGroup(tryMatchResult, group);
				const missingSlotLabels: MissingSlotData[] = Array.from({ length: myMissing }, (_, index) => {
					const matched = tryMatchResult != null && index < tryMatchResult.providedAmountAfterMatching;
					return {
						timeLabel: null,
						postedRequestId: matched ? tryMatchResult.requestId : null,
						postedTimeLabel: matched ? postedTimeLabel : null,
						startMin: group.startMin,
						endMin: group.endMin,
					};
				});

				return { required: myRequired, isBlocked: false, nurses, missingSlotLabels };
			}

			// Single-role behavior. Each ConfigSlot carries the originating config's time range so we can
			// call tryMatch per missing slot: a single posted shift covering multiple shorter configs
			// (e.g. 6a–6p covering 6a–2p + 2p–6p) correctly matches all of them via fractional budgeting.
			type ConfigSlot = { startMin: number; endMin: number; label: string | null };
			const configSlots: ConfigSlot[] = activeConfigs.flatMap((cfg) => {
				// Same score as `totalRequired`, so the slot count matches the number shown.
				const req = scoreAdjustedRequiredForLocation(
					cfg,
					resolveLocationCensus(cfg, dayCensus.byDimensions, dayCensus.totalCensus, deptStatsMode, ALLOW_AVAILABILITY_FALLBACK),
					deptStatsMode,
					dayName,
					toLadderFilter(requiredFilter),
					dayCensus.demoRequired,
				);
				const cfgStart = parseHHMMToMinutes(cfg.shift_start);
				const cfgEnd = parseHHMMToMinutes(cfg.shift_end);
				const label = cfgStart !== group.startMin || cfgEnd !== group.endMin ? formatMinutesToLabel(cfgStart, cfgEnd) : null;
				return Array.from({ length: req }, (): ConfigSlot => ({ startMin: cfgStart, endMin: cfgEnd, label }));
			});
			const singleRequired = totalRequired;
			const actualWeight = weightedActual(nurses);
			const missingSlotLabels: MissingSlotData[] = !singleRequired
				? []
				: configSlots.slice(/*Floor to get the minimum covered slots -> slice to get missing*/ Math.floor(actualWeight)).map((slot) => {
						const match =
							tryMatch?.(locationName, weekViewData[dayIdx].date, slot.startMin, slot.endMin, sectionRoles, 1, requiredFilter?.jobId ?? null) ?? null;
						return {
							timeLabel: slot.label,
							postedRequestId: match?.requestId ?? null,
							postedTimeLabel: postedLabelRelativeToGroup(match, group),
							startMin: slot.startMin,
							endMin: slot.endMin,
						};
					});

			return { required: singleRequired, isBlocked: false, nurses, missingSlotLabels };
		});

		const maxRequired = Math.max(0, ...dayData.map((d) => d.required));
		const maxActual = Math.max(0, ...dayData.map((d) => d.nurses.length));
		// numSlots drives row height — use raw nurse amounts count (not weighted) for layout
		const numSlots = Math.max(maxRequired, maxActual);

		if (numSlots === 0) continue;

		results.push({ locationName, locationShortName, configs, dayData, numSlots });
	}

	// The app's location order (`compareValues`: manual order, then floor, then name), as every location list and
	// dropdown uses — except the unassigned row (blank name), which stays on top where the grid has always shown it.
	results.sort((a, b) => Number(b.locationName === "") - Number(a.locationName === "") || compareValues(a.locationName, b.locationName, customLocationOrder));

	return results;
}

function isShiftApplicableToJob(shift: NurseInShift, jobId: string): boolean {
	return !!shift.jobId && shift.jobId === jobId;
}

// ─── Build role section helper ───────────────────────────────────────────────

export function buildRoleSection({
	getNurseNameFromId,
	sectionName,
	locations,
	weekViewData,
	allDeptRoles,
	filterByJobId,
	roles,
	tryMatch,
	dayCensusInputs,
	isGrouped,
	jobManagementEnabled,
	disableTimeGroupsOverlaps,
	customLocationOrder,
	timeOffByNurse,
	deptStatsMode,
	punchedShiftsByDay,
}: {
	sectionName: string;
	roles: NurseFullRole[];
	locations: DepartmentLocation[];
	weekViewData: DayItem[];
	getNurseNameFromId: (id: number) => string;
	isGrouped: boolean;
	/** True when the Job Management feature flag is on — suppresses the fabricated per-role split for multi-role locations (see buildComputedLocationsForGroup). */
	jobManagementEnabled: boolean;
	allDeptRoles?: NurseFullRole[];
	dayCensusInputs?: DayCensusInput[];
	tryMatch?: TryMatchFn;
	filterByJobId?: string;
	disableTimeGroupsOverlaps?: boolean;
	customLocationOrder?: Map<string, number> | null;
	timeOffByNurse?: Map<number, TimeOffRequest[]>;
	deptStatsMode: StatsMode;
	/** `null` for the whole week when the department never shows punched shifts. */
	punchedShiftsByDay: PunchedShiftsByDay | null;
}): RoleSection | null {
	// Location configs relevant to these roles/role groups or jobs. This can legitimately be empty:
	// a role/job may have scheduled shifts whose locations match no configured location. We must NOT
	// bail here — the section is still built from the shift-derived locations below (with required = 0)
	// so a nurse's shift is never hidden just because its location isn't in the department config. The
	// canonical location set is unique(configured locations ∪ scheduled-shift locations); see the union
	// in buildComputedLocationsForGroup. If neither configs nor shifts exist, the empty-timeTitles guard
	// at the end returns null naturally.
	const roleLocations = filterByJobId
		? locations.filter((loc) => loc.jobId === filterByJobId)
		: locations.filter((loc) => loc.location.trim() && loc.roles.some((r) => r === "all" || roles.includes(r)));

	// Restrict required-amount math to this section's dimension so a location's rungs for other
	// jobs/roles aren't counted here (e.g. a CNA rung must not inflate an RN section's required).
	const requiredFilter: { roles?: NurseRole[]; jobId?: string } = filterByJobId
		? { jobId: filterByJobId }
		: { roles: roles.map((r) => inverseRolesMap[r]).filter((r): r is NurseRole => r !== undefined) };

	// Step 1: derive time groups from actual week shifts
	const groups = computeTimeGroups(weekViewData, roles, filterByJobId, disableTimeGroupsOverlaps);

	// Step 2: assign location configs → groups
	const configsByGroup: Map<string, DepartmentLocation[]>[] = groups.map(() => new Map());
	const unboundConfigs: DepartmentLocation[] = [];

	function assignLocToGroups(loc: DepartmentLocation, targetGroups: TimeGroupConfig[], targetConfigsByGroup: Map<string, DepartmentLocation[]>[]) {
		const locStart = parseHHMMToMinutes(loc.shift_start);
		const locEnd = parseHHMMToMinutes(loc.shift_end);
		const locDuration = durationMinutes(locStart, locEnd);
		const locName = loc.location.trim();
		let containingIdx = -1;
		let bestDuration = Infinity;
		for (let i = 0; i < targetGroups.length; i++) {
			if (computeOverlapMinutes(locStart, locEnd, targetGroups[i].startMin, targetGroups[i].endMin) >= locDuration) {
				const gDur = durationMinutes(targetGroups[i].startMin, targetGroups[i].endMin);
				if (gDur < bestDuration) {
					bestDuration = gDur;
					containingIdx = i;
				}
			}
		}
		if (containingIdx >= 0) {
			const bucket = targetConfigsByGroup[containingIdx];
			const existing = bucket.get(locName);
			if (existing) existing.push(loc);
			else bucket.set(locName, [loc]);
			return true;
		}
		let matched = false;
		for (let gi = 0; gi < targetGroups.length; gi++) {
			const g = targetGroups[gi];
			const overlap = computeOverlapMinutes(locStart, locEnd, g.startMin, g.endMin);
			if (overlap / locDuration >= 0.5) {
				const bucket = targetConfigsByGroup[gi];
				const existing = bucket.get(locName);
				if (existing) existing.push(loc);
				else bucket.set(locName, [loc]);
				matched = true;
			}
		}
		return matched;
	}

	for (const loc of roleLocations) {
		if (!assignLocToGroups(loc, groups, configsByGroup)) {
			unboundConfigs.push(loc);
		}
	}

	// Step 2b: extend groups from unmatched config times
	if (unboundConfigs.length > 0) {
		const configGroups = buildConfigDerivedGroups(unboundConfigs);
		const newConfigsByGroup: Map<string, DepartmentLocation[]>[] = configGroups.map(() => new Map());
		for (const loc of unboundConfigs) {
			assignLocToGroups(loc, configGroups, newConfigsByGroup);
		}
		groups.push(...configGroups);
		configsByGroup.push(...newConfigsByGroup);
	}

	// Step 2c: sort all groups by start time ascending
	const sortOrder = groups.map((_, i) => i).sort((a, b) => groups[a].startMin - groups[b].startMin);
	const sortedGroups = sortOrder.map((i) => groups[i]);
	const sortedConfigsByGroup = sortOrder.map((i) => configsByGroup[i]);
	groups.length = 0;
	groups.push(...sortedGroups);
	configsByGroup.length = 0;
	configsByGroup.push(...sortedConfigsByGroup);

	// Step 2d: ensure every shift has at least one overlapping group so data is never hidden.
	// Shifts whose exact times produce no overlap with any existing group get a dedicated group.
	const rolesSet = new Set(roles);
	let addedFallbackGroups = false;
	for (const { shifts } of weekViewData) {
		for (const shift of shifts) {
			if (filterByJobId ? !isShiftApplicableToJob(shift, filterByJobId) : !rolesSet.has(shift.role)) continue;
			const shiftStart = shift.startTime.hour * 60 + shift.startTime.minute;
			const shiftEnd = shift.endTime.hour * 60 + shift.endTime.minute;
			const hasOverlap = groups.some((g) => computeOverlapMinutes(shiftStart, shiftEnd, g.startMin, g.endMin) > 0);
			if (!hasOverlap) {
				groups.push({ startMin: shiftStart, endMin: shiftEnd, label: formatMinutesToLabel(shiftStart, shiftEnd) });
				configsByGroup.push(new Map());
				addedFallbackGroups = true;
			}
		}
	}
	if (addedFallbackGroups) {
		// Re-sort after adding fallback groups
		const sortOrder2 = groups.map((_, i) => i).sort((a, b) => groups[a].startMin - groups[b].startMin);
		const sortedGroups2 = sortOrder2.map((i) => groups[i]);
		const sortedConfigsByGroup2 = sortOrder2.map((i) => configsByGroup[i]);
		groups.length = 0;
		groups.push(...sortedGroups2);
		configsByGroup.length = 0;
		configsByGroup.push(...sortedConfigsByGroup2);
	}

	// Step 3: assign shifts → groups (per day). Groups above always come from the schedule, so the
	// layout does not move when a past day shows punched shifts instead.
	const shiftsByDayAndGroup: Map<string, WeightedShift[]>[][] =
		groups.length > 0 ? buildShiftsByGroupForWeek(weekViewData, punchedShiftsByDay, roles, groups, locations, filterByJobId) : [];

	// For non-grouped mode with multi-role configs: build all-role shifts to determine cross-role frequency
	const allRoleShifts: Map<string, WeightedShift[]>[][] | undefined =
		!isGrouped && allDeptRoles && groups.length > 0
			? buildShiftsByGroupForWeek(weekViewData, punchedShiftsByDay, allDeptRoles, groups, locations, filterByJobId)
			: undefined;

	// Step 4: build TimeTitles
	const timeTitles: TimeTitle[] = groups
		.map((group, gi) => {
			const locations = buildComputedLocationsForGroup(
				configsByGroup[gi],
				gi,
				group,
				shiftsByDayAndGroup,
				weekViewData,
				getNurseNameFromId,
				isGrouped,
				jobManagementEnabled,
				roles,
				deptStatsMode,
				allRoleShifts,
				dayCensusInputs,
				tryMatch,
				customLocationOrder,
				timeOffByNurse,
				requiredFilter,
			);
			const dayTotals = weekViewData.map((_, dayIdx) => ({
				actual: locations.reduce((s, cl) => s + weightedActual(cl.dayData[dayIdx].nurses), 0),
				required: locations.reduce((s, cl) => s + cl.dayData[dayIdx].required, 0),
			}));
			return { label: group.label, startMin: group.startMin, endMin: group.endMin, locations, dayTotals };
		})
		.filter((t) => t.locations.length > 0);

	if (timeTitles.length === 0) return null;

	return { shortRole: sectionName, timeTitles };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useWeeklyGapView(
	weekViewData: DayItem[],
	groupingType: WeeklyGroupingType = WeeklyGroupingType.Role,
	selectedContractSections: ReadonlySet<string> = EMPTY_SECTIONS,
): { roleSections: RoleSection[]; dataProblems: WeeklyGapDataProblem[] } {
	const { chosenDepartment, getNurseNameFromId, hospitalData, getWeekIndexByDate, getDayIndexByDate, isHospitalView } = useHospitalData();
	const isRealHospitalDemo = isHospitalView;
	const enabledJobs = useJobs();
	const { settings } = useUserSettingsContext();
	const { staffRequests } = useRequestsContext();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const isTimeOffRequestsEnabled = useFeatureFlag(FEATURE_FLAGS.TimeOffRequestsEnabled);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { data: timeOffRequests } = useGetDepartmentTimeOffRequests();

	// The full per-day census_grouping object; resolveLocationCensus picks the right per-mode map
	// (incl. Auto-by-name aggregation and cross-dimension fallback) per location, keeping weekly in
	// sync with the daily view.
	const dayCensusInputs = useMemo<DayCensusInput[]>(
		() =>
			weekViewData.map((dayItem) => {
				const weekIndex = getWeekIndexByDate(dayItem.date);
				const dayIndex = getDayIndexByDate(dayItem.date);
				const week = hospitalData.weeks[weekIndex];
				return {
					byDimensions: byDimensionsOf(week?.weeklyShifts[0]?.days[dayIndex]?.census_grouping),
					totalCensus: week === undefined ? 0 : getCensus(week, dayIndex),
					demoRequired: demoRequiredForDay({ department: chosenDepartment, week, dayIndex, isRealHospitalDemo }),
				};
			}),
		[weekViewData, getWeekIndexByDate, getDayIndexByDate, hospitalData.weeks, isRealHospitalDemo, chosenDepartment],
	);

	// Demo departments that count punches: a past day with punches shows the punched shifts.
	const punchedByDay = useMemo<(PunchedShift[] | null)[] | null>(() => {
		if (!countsPunchesOnPastDays(chosenDepartment.department_id)) return null;
		const now = DateTime.now();
		return weekViewData.map((dayItem) =>
			isPastDayWithPunches(dayItem.date, dayItem.shiftsActual, now) ? punchedShiftsOnDate(hospitalData.weeks, dayItem.date) : null,
		);
	}, [chosenDepartment.department_id, weekViewData, hospitalData.weeks]);

	const { roleSections, noGroupPunches } = useMemo(() => {
		const noGroupPunches = new Set<PunchedShift>();
		const punchedShiftsByDay = punchedByDay === null ? null : { byDay: punchedByDay, noGroup: noGroupPunches };

		// All department roles as NurseFullRole[] — used for cross-role frequency in non-grouped mode
		const allDeptFullRoles: NurseFullRole[] = chosenDepartment.roles.map((sr) => rolesMap[sr]).filter((r): r is NurseFullRole => r !== undefined);

		// Build nurse → time off requests lookup for O(1) per-cell matching
		let timeOffByNurse: Map<number, TimeOffRequest[]> | undefined;
		if (isTimeOffRequestsEnabled && timeOffRequests?.length) {
			timeOffByNurse = new Map<number, TimeOffRequest[]>();
			for (const req of timeOffRequests) {
				if (req.deleted) continue;
				const list = timeOffByNurse.get(req.requestingStaffId);
				if (list) list.push(req);
				else timeOffByNurse.set(req.requestingStaffId, [req]);
			}
		}

		// Builds the full set of sections for one matching pass. Declares its OWN fresh roleSections
		// so each pass gets an independent array (the passed-in `tryMatch` carries the pass's budgets).
		// timeOffByNurse / allDeptFullRoles / dept config are read-only here, so they are computed once
		// above and reused across both passes.
		const buildSections = (tryMatch: TryMatchFn): RoleSection[] => {
			const roleSections: RoleSection[] = [];
			if (groupingType === WeeklyGroupingType.Jobs && enabledJobs.length) {
				// Group by job: one section per job
				for (const job of [...enabledJobs].sort((a, b) => a.sortOrder - b.sortOrder)) {
					const section = buildRoleSection({
						sectionName: job.displayName,
						roles: allDeptFullRoles,
						locations: chosenDepartment.locations,
						weekViewData,
						getNurseNameFromId,
						isGrouped: true,
						jobManagementEnabled,
						allDeptRoles: allDeptFullRoles,
						dayCensusInputs,
						tryMatch,
						filterByJobId: job.id,
						disableTimeGroupsOverlaps: settings.disableTimeGroupsOverlaps,
						customLocationOrder: customStringOrderMap,
						timeOffByNurse,
						deptStatsMode: chosenDepartment.statsMode,
						punchedShiftsByDay,
					});

					if (section) {
						section.isJobSection = true;
						section.jobId = job.id;
						roleSections.push(section);
					}
				}
			} else if (groupingType === WeeklyGroupingType.RoleGroups && chosenDepartment.role_groups && Object.keys(chosenDepartment.role_groups).length > 0) {
				// Track which individual roles are covered by a group
				const coveredRoles = new Set<string>();

				for (const [groupName, groupShortRoles] of Object.entries(chosenDepartment.role_groups)) {
					const fullRoles: NurseFullRole[] = groupShortRoles.map((sr) => rolesMap[sr as NurseRole]).filter((r): r is NurseFullRole => r !== undefined);

					if (fullRoles.length === 0) continue;

					// Only include roles that the department actually has
					const deptFullRoles = fullRoles.filter((fr) => chosenDepartment.roles.some((sr) => rolesMap[sr] === fr));
					if (deptFullRoles.length === 0) continue;

					for (const sr of groupShortRoles) {
						coveredRoles.add(sr);
					}

					const section = buildRoleSection({
						sectionName: groupName,
						roles: deptFullRoles,
						locations: chosenDepartment.locations,
						weekViewData,
						getNurseNameFromId,
						isGrouped: true,
						jobManagementEnabled,
						dayCensusInputs,
						tryMatch,
						disableTimeGroupsOverlaps: settings.disableTimeGroupsOverlaps,
						customLocationOrder: customStringOrderMap,
						timeOffByNurse,
						deptStatsMode: chosenDepartment.statsMode,
						punchedShiftsByDay,
					});
					if (section) roleSections.push(section);
				}

				// Add individual sections for roles not in any group
				for (const shortRole of chosenDepartment.roles) {
					if (coveredRoles.has(shortRole)) continue;
					const nurseFullRole = rolesMap[shortRole];
					const section = buildRoleSection({
						sectionName: shortRole,
						roles: [nurseFullRole],
						locations: chosenDepartment.locations,
						weekViewData,
						getNurseNameFromId,
						isGrouped: false,
						jobManagementEnabled,
						allDeptRoles: allDeptFullRoles,
						dayCensusInputs,
						tryMatch,
						disableTimeGroupsOverlaps: settings.disableTimeGroupsOverlaps,
						customLocationOrder: customStringOrderMap,
						timeOffByNurse,
						deptStatsMode: chosenDepartment.statsMode,
						punchedShiftsByDay,
					});
					if (section) roleSections.push(section);
				}
			} else {
				// Default: one section per individual role
				for (const shortRole of chosenDepartment.roles) {
					const nurseFullRole = rolesMap[shortRole];
					const section = buildRoleSection({
						sectionName: shortRole,
						roles: [nurseFullRole],
						locations: chosenDepartment.locations,
						weekViewData,
						getNurseNameFromId,
						isGrouped: false,
						jobManagementEnabled,
						allDeptRoles: allDeptFullRoles,
						dayCensusInputs,
						tryMatch,
						disableTimeGroupsOverlaps: settings.disableTimeGroupsOverlaps,
						customLocationOrder: customStringOrderMap,
						timeOffByNurse,
						deptStatsMode: chosenDepartment.statsMode,
						punchedShiftsByDay,
					});
					if (section) roleSections.push(section);
				}
			}
			return roleSections;
		};

		// Two passes over the section builder. The strict-vs-fallback (in regard to time dimension) decision is per-request AND
		// global: a posted request falls back to loose (any-overlap) matching only if it found NO
		// strict (half-overlap-gated) home anywhere across all sections. A single per-cell pass can't
		// express that — a cell can't know whether a request will find a strict home in a later
		// section. So pass 1 probes with everything strict to discover which requests matched strictly
		// somewhere (result discarded), then pass 2 rebuilds treating only those as strict and letting
		// the rest fall back. Tradeoff: two full section builds per compute — this re-derives the whole
		// time-group/section layout (computeTimeGroups + buildShiftsByGroupForDay over every day×group)
		// twice, not just the matcher. Accepted for correctness; if it ever shows up on large
		// departments, split the (matcher-independent) layout derivation out to compute once.
		const strictlyMatched = new Set<string>();
		const probe = createPostedRequestMatcher(staffRequests, { isStrict: () => true, onMatched: (id) => strictlyMatched.add(id) });
		buildSections(probe.tryMatch);

		const final = createPostedRequestMatcher(staffRequests, { isStrict: (id) => strictlyMatched.has(id) });
		const roleSections = buildSections(final.tryMatch);

		// Final step — apply Contract Type filter by removing non-matching nurse
		// cells, then recompute the per-day `actual` totals from the surviving
		// cells so day headers stay in sync with what's rendered. Layout values
		// (required, numSlots, isBlocked, missingSlotLabels, roleCounts) are
		// intentionally NOT recomputed: the time-group layout and slot counts
		// are derived from the full shift set, so keeping them stable prevents
		// the grid from collapsing when the filter changes.
		if (selectedContractSections.size > 0) {
			for (const section of roleSections) {
				for (const title of section.timeTitles) {
					for (const loc of title.locations) {
						for (const day of loc.dayData) {
							day.nurses = day.nurses.filter((slot) => matchesContractSections(slot.nurseInShift.type, selectedContractSections));
						}
					}
					title.dayTotals = title.dayTotals.map((total, dayIdx) => ({
						actual: title.locations.reduce((sum, loc) => sum + weightedActual(loc.dayData[dayIdx].nurses), 0),
						required: total.required,
					}));
				}
			}
		}

		return { roleSections, noGroupPunches };
	}, [
		chosenDepartment,
		enabledJobs,
		weekViewData,
		getNurseNameFromId,
		groupingType,
		dayCensusInputs,
		settings.disableTimeGroupsOverlaps,
		staffRequests,
		customStringOrderMap,
		isTimeOffRequestsEnabled,
		jobManagementEnabled,
		timeOffRequests,
		selectedContractSections,
		punchedByDay,
	]);

	// Demo setup problems found while building. The builders are plain functions, so the component reports these.
	const dataProblems = useMemo<WeeklyGapDataProblem[]>(() => {
		const nodeSplitProblems = dayCensusInputs.flatMap(({ demoRequired }) =>
			demoRequired?.kind === "node"
				? demoRequired.problems.map(({ message, details }) => ({ event: "demo_node_split_error", properties: { error_message: message, ...details } }))
				: [],
		);
		const punchProblems = [...noGroupPunches].map(({ shift, shiftType }) => ({
			event: "weekly_gaps_punched_shift_no_group",
			properties: { error_message: "Weekly gaps: no group for a punched shift", shift_id: shift.id, shift_type: shiftType },
		}));
		return [...nodeSplitProblems, ...punchProblems];
	}, [dayCensusInputs, noGroupPunches]);

	return { roleSections, dataProblems };
}
